import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import GeneralTable from "../../../components/Table/GeneralTable";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IconButton from "../../../components/IconButton/IconButton";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";

function IncomeTax() {
  const [incomeTaxSlabs, setIncomeTaxSlabs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true); // Start loading before API call
    axiosInstance
      .get(`${process.env.REACT_APP_API_BASE_URL}/income-tax-slabs`)
      .then((response) => {
        const data = response.data.data;
        if (Array.isArray(data) && data.length > 0) {
          setIncomeTaxSlabs(
            data.map((slab, index) => ({
              ...slab,
              slabName:
                slab.slabName || `Slab ${slab.slabSequence || index + 1}`,
              minValue: slab.slabMinValue,
              maxValue: slab.slabMaxValue === 0 ? null : slab.slabMaxValue,
              percentage: slab.slabPercentage,
            })),
          );
        } else {
          console.log("No slabs found. Adding a blank slab.");
          addNewSlab();
        }
      })
      .catch((error) =>
        console.error("Error fetching income tax slabs:", error),
      )
      .finally(() => setLoading(false)); // Stop loading after API call completes
  }, []);

  const handleIncomeTaxChange = (index, field, value) => {
    const updatedSlabs = [...incomeTaxSlabs];
    if (
      field === "percentage" ||
      field === "minValue" ||
      field === "maxValue"
    ) {
      updatedSlabs[index][field] = value === "" ? null : parseFloat(value);
    } else {
      updatedSlabs[index][field] = value;
    }
    setIncomeTaxSlabs(updatedSlabs);
  };

  const saveIncomeTaxSlabs = () => {
    const payload = incomeTaxSlabs.map((slab, index) => ({
      incomeTaxId: slab.incomeTaxId || 0,
      slabName: slab.slabName,
      slabSequence: index + 1,
      slabMinValue: slab.minValue,
      slabMaxValue: slab.maxValue || 0,
      slabPercentage: slab.percentage,
      paymentCalculationBasedOn: "GROSS_SALARY",
    }));

    axiosInstance
      .post(`${process.env.REACT_APP_API_BASE_URL}/income-tax-slabs`, payload)
      .then((response) => {
        const data = response.data;
        if (data && data.message) {
          toast.success(data.message);
        }
        console.log("Saved Income Tax Slabs:", data);
      })
      .catch((error) => console.error("Error saving income tax slabs:", error));
  };

  const deleteIncomeTaxSlab = (incomeTaxId, index) => {
    if (incomeTaxId) {
      axiosInstance
        .delete(
          `${process.env.REACT_APP_API_BASE_URL}/income-tax-slabs/${incomeTaxId}`,
        )
        .then((response) => {
          const data = response.data;
          if (data && data.message) {
            toast.success(data.message);
          }
          removeSlab(index);
        })
        .catch((error) =>
          console.error("Error deleting income tax slab:", error),
        );
    } else {
      removeSlab(index);
    }
  };

  const removeSlab = (index) => {
    setIncomeTaxSlabs((prevSlabs) => prevSlabs.filter((_, i) => i !== index));
  };

  const addNewSlab = () => {
    setIncomeTaxSlabs((prevSlabs) => [
      ...prevSlabs,
      {
        slabName: `Slab ${prevSlabs.length + 1}`,
        minValue: null,
        maxValue: null,
        percentage: null,
      },
    ]);
  };

  // Define columns for GeneralTable
  const columns = [
    {
      header: "Slab Name",
      accessor: "slabName",
    },
    {
      header: "Min Value",
      accessor: "minValue",
    },
    {
      header: "Max Value",
      accessor: "maxValue",
    },
    {
      header: "Percentage",
      accessor: "percentage",
    },
    {
      header: "Actions",
      accessor: "actions",
    },
  ];

  // Transform data to include input fields and actions
  const tableData = incomeTaxSlabs.map((slab, index) => ({
    ...slab,
    slabName: (
      <input
        type="text"
        value={slab.slabName}
        onChange={(e) =>
          handleIncomeTaxChange(index, "slabName", e.target.value)
        }
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "10px",
        }}
      />
    ),
    minValue: (
      <input
        type="number"
        value={
          slab.minValue === null || slab.minValue === undefined
            ? ""
            : slab.minValue
        }
        onChange={(e) =>
          handleIncomeTaxChange(index, "minValue", e.target.value)
        }
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "10px",
        }}
      />
    ),
    maxValue: (
      <input
        type="number"
        placeholder=">"
        value={
          slab.maxValue === null || slab.maxValue === undefined
            ? ""
            : slab.maxValue
        }
        onChange={(e) =>
          handleIncomeTaxChange(index, "maxValue", e.target.value)
        }
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "10px",
        }}
      />
    ),
    percentage: (
      <input
        type="number"
        min="0"
        value={slab.percentage ?? ""}
        onChange={(e) =>
          handleIncomeTaxChange(index, "percentage", e.target.value)
        }
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "10px",
        }}
      />
    ),
    actions: (
      <IconButton
        className="delete-slab"
        onClick={() => deleteIncomeTaxSlab(slab.incomeTaxId, index)}
        variant="delete"
        icon="fluent:delete-28-regular"
        title="Delete Tax"
      />
    ),
  }));

  if (loading) return <LoadingScreen />;

  return (
    <div className="income-tax-container">
      <h1 className="display6">Income Tax</h1>

      {/* Income Tax Slabs Table using GeneralTable */}
      <GeneralTable
        columns={columns}
        data={tableData}
        title="Income Tax Slabs"
      />

      <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
        <ButtonWithIcon
          className="edit-button"
          onClick={addNewSlab}
          type="success"
          text="Add New Slab"
        />
        <ButtonWithIcon
          onClick={saveIncomeTaxSlabs}
          className="submit-button"
          text="Save"
        />
      </div>
    </div>
  );
}

export default IncomeTax;
